import {
  EmailAuthProvider,
  GoogleAuthProvider,
  linkWithCredential,
  reauthenticateWithPopup,
  signInWithEmailAndPassword,
  signInWithPopup,
  updatePassword,
} from 'firebase/auth';
import { auth } from '../firebase';
import { ensureUserDoc } from './user';

const MIN_PASSWORD_LENGTH = 8;

/** Map Firebase Auth errors to short, user-facing copy. */
export function friendlyAuthError(err) {
  const code = err?.code || '';
  switch (code) {
    case 'auth/invalid-credential':
    case 'auth/wrong-password':
    case 'auth/user-not-found':
      return 'Incorrect email or password.';
    case 'auth/invalid-email':
      return 'Enter a valid email address.';
    case 'auth/weak-password':
      return `Use at least ${MIN_PASSWORD_LENGTH} characters.`;
    case 'auth/email-already-in-use':
    case 'auth/credential-already-in-use':
      return 'That email is already linked to another account.';
    case 'auth/provider-already-linked':
      return 'Password sign-in is already set up on this account.';
    case 'auth/requires-recent-login':
      return 'For security, sign in again, then set your password.';
    case 'auth/too-many-requests':
      return 'Too many attempts. Try again in a few minutes.';
    case 'auth/popup-closed-by-user':
      return 'Sign-in was cancelled.';
    case 'auth/network-request-failed':
      return 'Network error. Check your connection and try again.';
    default:
      return err?.message || 'Sign-in failed.';
  }
}

export function hasPasswordProvider(user = auth.currentUser) {
  return Boolean(user?.providerData?.some((p) => p.providerId === 'password'));
}

export function validateNewPassword(password, confirm) {
  if (!password || password.length < MIN_PASSWORD_LENGTH) {
    return `Password must be at least ${MIN_PASSWORD_LENGTH} characters.`;
  }
  if (password !== confirm) {
    return 'Passwords do not match.';
  }
  return null;
}

export async function signInWithGoogle() {
  const result = await signInWithPopup(auth, new GoogleAuthProvider());
  if (result.user) await ensureUserDoc(result.user);
  return result;
}

export async function signInWithCredentials(email, password) {
  const result = await signInWithEmailAndPassword(auth, email.trim(), password);
  if (result.user) await ensureUserDoc(result.user);
  return result;
}

/**
 * Set or update a password for the signed-in user so they can use
 * "Log in with credentials" later. Google-only accounts are linked via
 * EmailAuthProvider; accounts that already have password use updatePassword.
 * If Firebase requires recent login, reauths with Google then retries once.
 */
export async function setAccountPassword(password, confirm) {
  const user = auth.currentUser;
  if (!user) throw new Error('Sign in before setting a password.');

  const validation = validateNewPassword(password, confirm);
  if (validation) throw new Error(validation);

  const email = (user.email || '').trim();
  if (!email) {
    throw new Error('Your account needs an email address before you can set a password.');
  }

  const apply = async () => {
    if (hasPasswordProvider(user)) {
      await updatePassword(user, password);
      return { linked: false };
    }
    const credential = EmailAuthProvider.credential(email, password);
    await linkWithCredential(user, credential);
    return { linked: true };
  };

  try {
    return await apply();
  } catch (err) {
    if (err?.code !== 'auth/requires-recent-login') throw err;
    const hasGoogle = user.providerData?.some((p) => p.providerId === 'google.com');
    if (!hasGoogle) throw err;
    await reauthenticateWithPopup(user, new GoogleAuthProvider());
    return apply();
  }
}
